import React, { useEffect, useRef, useState } from "react";
import { Button } from "../ui";
import { useTranslation } from "react-i18next";
import { api } from "../../services";
import { toast } from "react-toastify";
import { getLocalizedPlatformName } from "../../utils/platformNames";

const getSyncConfirmMessage = (tile, itemCount, t) => {
  const appLabel = getLocalizedPlatformName(
    tile?.application || tile?.name || tile,
    t
  );
  const lower = String(tile?.application || tile?.name || tile || "").toLowerCase();

  if (lower.includes("ninja")) {
    return t(
      "Syncing with Ninja will create (in Pending state) or update the MENU. Branch Availability hours will also be submitted with Catalog Sync. Do you want to continue?",
      { defaultValue: t("catalogSync.ninja") }
    );
  }
  if (lower.includes("keeta")) {
    if (Number(itemCount) < 5) {
      return t("catalogSync.keetaLessThan5");
    }
    return t("catalogSync.keetaDefault");
  }
  return t("catalogSync.default", { app: appLabel });
};

const CatalogSyncModal = ({
  modalShow = false,
  tile,
  catalogId,
  appId,
  storeId,
  itemCount = 0,
  onClose,
  onSyncSuccess,
  syncAllowed = true,
}) => {
  const { t, i18n } = useTranslation("translation");
  const isArabic = i18n.language === "ar" || i18n.dir() === "rtl";
  const dropdownRef = useRef(null);
  const cancelButtonRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const fetchBranches = async () => {
    if (!storeId) return;
    try {
      const { data } = await api.get(
        `/v2.1/catalog/${catalogId}/app/${appId}/synced-branches`
      );
      setBranches(data?.data.syncedBranches || []);
    } catch (err) {
      console.error("Failed to fetch branches", err);
    }
  };

  useEffect(() => {
    if (modalShow && catalogId && appId) {
      fetchBranches();
      setSelectedBranches([]);
    }
  }, [modalShow, catalogId, appId, storeId]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
        setHighlightedIndex(-1);
      }
    };
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && !isLoading) {
        handleClose();
      }
    };

    if (modalShow) {
      document.addEventListener("keydown", handleKeyDown);
      if (cancelButtonRef.current) {
        cancelButtonRef.current.focus();
      }
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [modalShow, isLoading]);

  const handleClose = () => {
    setSelectedBranches([]);
    setFilter("");
    setDropdownOpen(false);
    setHighlightedIndex(-1);
    if (onClose) onClose();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isLoading) {
      handleClose();
    }
  };

  const handleSync = async () => {
    if (!syncAllowed) {
      toast.error(t("noPermissionToAccessThis"));
      return;
    }
    if (selectedBranches.length === 0) {
      toast.error(t("catalogSyncModal.selectBranchError"));
      return;
    }

    setIsLoading(true);
    try {
      const token = localStorage.getItem("token");
      const payload = { catalogId, appId, branchIds: selectedBranches };

      const response = await api.post("/v2.1/catalog/sync", payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      toast.success(
        t(response.data?.message || "catalogSyncModal.syncSuccess", {
          defaultValue:
            response.data?.message || t("catalogSyncModal.syncSuccess"),
        })
      );
      if (onSyncSuccess) onSyncSuccess();
      handleClose();
    } catch (error) {
      console.error("Error syncing catalog:", error);
      const errorMessage =
        error.response?.data?.message || t("catalogSyncModal.syncError");
      toast.error(t(errorMessage, { defaultValue: errorMessage }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectBranch = (branch) => {
    if (!branch?._id) return;
    setSelectedBranches((prev) =>
      prev.includes(branch._id)
        ? prev.filter((id) => id !== branch._id)
        : [...prev, branch._id]
    );
    setHighlightedIndex(-1);
  };

  const handleRemoveTag = (id) => {
    setSelectedBranches((prev) => prev.filter((branchId) => branchId !== id));
  };

  const handleSelectAll = () => {
    setSelectedBranches(branches.map((b) => b._id));
  };

  const handleUnselectAll = () => {
    setSelectedBranches([]);
  };

  const filteredBranches = branches.filter(
    (b) =>
      !selectedBranches.includes(b._id) &&
      b.name.toLowerCase().includes(filter.toLowerCase())
  );

  const handleKeyDown = (e) => {
    if (!dropdownOpen) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev < filteredBranches.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev > 0 ? prev - 1 : filteredBranches.length - 1
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex >= 0 && filteredBranches[highlightedIndex]) {
        handleSelectBranch(filteredBranches[highlightedIndex]);
      }
    }
  };

  if (!modalShow) return null;

  const platformLabel = getLocalizedPlatformName(
    tile?.application || tile?.name || tile,
    t
  );
  const confirmMessage = getSyncConfirmMessage(tile, itemCount, t);

  const syncButton = (
    <Button
      type="button"
      color="primary"
      size="md"
      onClick={handleSync}
      disabled={!syncAllowed || isLoading || selectedBranches.length === 0}
      title={!syncAllowed ? t("noPermissionToAccessThis") : undefined}
    >
      {isLoading ? (
        <>
          <span
            className="spinner-border spinner-border-sm me-2"
            role="status"
            aria-hidden="true"
          />
          {t("catalogSyncModal.syncing")}
        </>
      ) : (
        t("catalogSync.syncButton")
      )}
    </Button>
  );

  const cancelButton = (
    <Button
      ref={cancelButtonRef}
      type="button"
      color="secondary"
      size="md"
      onClick={handleClose}
      disabled={isLoading}
      className={isArabic ? "" : "me-2"}
    >
      {t("catalogSyncModal.cancelButton")}
    </Button>
  );

  return (
    <div
      className="modal fade show d-block oh-modal oh-modal--catalog-sync"
      dir={isArabic ? "rtl" : "ltr"}
      lang={i18n.language}
      style={{
        backgroundColor: "rgba(0,0,0,0.5)",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
      }}
      onClick={handleBackdropClick}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {t("Confirm Sync")}
              {platformLabel ? ` — ${platformLabel}` : ""}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={handleClose}
              disabled={isLoading}
              aria-label={t("close")}
            />
          </div>

          <div className="modal-body">
            <p className="mb-3" style={{ fontSize: "14px", lineHeight: 1.6 }}>
              {confirmMessage}
            </p>

            <div className="oh-field" ref={dropdownRef}>
              <label className="oh-field__label">
                {t("catalogSyncModal.branches")}
              </label>
              <div
                className="oh-modal-branch-picker"
                onClick={async () => {
                  if (!dropdownOpen) {
                    await fetchBranches();
                  }
                  setDropdownOpen(!dropdownOpen);
                }}
                onKeyDown={handleKeyDown}
                tabIndex={0}
                role="combobox"
                aria-expanded={dropdownOpen}
              >
                {selectedBranches.length === 0 && (
                  <span className="oh-modal-branch-picker__placeholder">
                    {t("catalogSyncModal.choose")}
                  </span>
                )}
                {selectedBranches.map((id) => {
                  const branch = branches.find((b) => b._id === id);
                  if (!branch) return null;
                  return (
                    <span key={id} className="oh-modal-branch-tag">
                      {branch.name}
                      <button
                        type="button"
                        className="oh-modal-branch-tag__remove"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveTag(id);
                        }}
                        aria-label={t("remove", { defaultValue: "Remove" })}
                      >
                        ×
                      </button>
                    </span>
                  );
                })}
              </div>

              {dropdownOpen && (
                <div className="oh-modal-branch-menu">
                  <input
                    type="text"
                    className="oh-field__control form-control form-control-sm mb-2"
                    placeholder={t("catalogSyncModal.filter")}
                    value={filter}
                    onChange={(e) => {
                      setFilter(e.target.value);
                      setHighlightedIndex(-1);
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <ul className="oh-modal-branch-menu__list">
                    {filteredBranches.length > 0 ? (
                      filteredBranches.map((branch, index) => (
                        <li
                          key={branch._id}
                          className={`oh-modal-branch-menu__item${
                            highlightedIndex === index ? " is-highlighted" : ""
                          }`}
                          onClick={() => handleSelectBranch(branch)}
                        >
                          {branch.name}
                        </li>
                      ))
                    ) : (
                      <li className="oh-modal-branch-menu__item is-muted">
                        {t("catalogSyncModal.noBranches")}
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>

            <div className="d-flex flex-wrap gap-2">
              {selectedBranches.length > 0 && (
                <Button
                  type="button"
                  color="secondary"
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleUnselectAll}
                >
                  {t("catalogSyncModal.unselectAll")}
                </Button>
              )}
              {selectedBranches.length !== branches.length &&
                branches.length > 0 && (
                  <Button
                    type="button"
                    color="primary"
                    variant="outline-primary"
                    size="sm"
                    onClick={handleSelectAll}
                  >
                    {t("catalogSyncModal.selectAll")}
                  </Button>
                )}
            </div>
          </div>

          <div className="modal-footer justify-content-end">
            {isArabic ? (
              <>
                {syncButton}
                {cancelButton}
              </>
            ) : (
              <>
                {cancelButton}
                {syncButton}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CatalogSyncModal;
